package fr.manyverse.app.presentation.components.player_screen.character

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.AddAPhoto
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Groups3
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SecondaryTabRow
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.layout.layout
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.presentation.components.rememberImagePickerLauncher
import fr.manyverse.app.presentation.components.ZoomableImageDialog
import fr.manyverse.app.presentation.components.CharacterAvatar
import fr.manyverse.app.presentation.components.player_screen.character.choice.CharacterChoiceButton
import fr.manyverse.app.presentation.components.player_screen.character.choice.ChoiceLoadingDialog
import fr.manyverse.app.presentation.components.player_screen.character.choice.CreateCharacterInTableDialog
import fr.manyverse.app.presentation.components.player_screen.character.choice.SelectCharacterDialog
import fr.manyverse.app.presentation.components.player_screen.character.choice.SelectPreMadeDialog
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.TableCharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character_informations
import manyverse.shared.generated.resources.choose_existing_character
import manyverse.shared.generated.resources.choose_premade
import manyverse.shared.generated.resources.create_character
import manyverse.shared.generated.resources.edit_avatar
import manyverse.shared.generated.resources.edit_character_name
import manyverse.shared.generated.resources.error_loading_characters
import manyverse.shared.generated.resources.error_loading_premades
import manyverse.shared.generated.resources.loading
import manyverse.shared.generated.resources.character_sheet_tab
import manyverse.shared.generated.resources.no_character_yet
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.math.roundToInt

@Composable
fun CharacterTab(
    character: Character?,
    isFreeConstruction: Boolean,
    isCharacterSheetAvailable: Boolean,
    tableId: String,
    userId: String,
    universeId: String,
    modifier: Modifier = Modifier
) {
    val characterViewModel: TableCharacterViewModel = koinViewModel(
        key = "table_character_${tableId}"
    ) { parametersOf(tableId, userId, universeId) }

    if (character == null) {
        val availabilityState by characterViewModel.choiceAvailability
            .collectAsStateWithLifecycle()
        val charactersState by characterViewModel.charactersState.collectAsStateWithLifecycle()
        val preMadesState by characterViewModel.preMadesState.collectAsStateWithLifecycle()

        LaunchedEffect(Unit) { characterViewModel.loadChoiceAvailability() }

        // Tant que la vérification est en cours, les deux boutons conditionnels affichent un
        // indicateur ; en cas d'échec du comptage on les laisse actifs (l'ouverture de la boîte
        // de dialogue peut très bien aboutir), et c'est elle qui rapportera l'erreur.
        val availability = (availabilityState as? AsyncUiState.Success)?.data
        val isCheckingChoices = availability == null && availabilityState !is AsyncUiState.Error
        val canChooseExisting = availability?.hasExistingCharacters ?: true
        val canChoosePreMade = availability?.hasPreMades ?: true

        var showCreateDialog by remember { mutableStateOf(false) }
        var showSelectDialog by remember { mutableStateOf(false) }
        var showPreMadeDialog by remember { mutableStateOf(false) }

        Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(32.dp)
            ) {
                Text(
                    text = stringResource(Res.string.no_character_yet),
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )

                @OptIn(ExperimentalLayoutApi::class)
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(
                        24.dp,
                        Alignment.CenterHorizontally
                    ),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    CharacterChoiceButton(
                        label = stringResource(Res.string.create_character),
                        icon = Icons.Default.Add,
                        onClick = { showCreateDialog = true }
                    )

                    CharacterChoiceButton(
                        label = stringResource(Res.string.choose_existing_character),
                        icon = Icons.Default.Person,
                        onClick = {
                            characterViewModel.loadExistingCharacters()
                            showSelectDialog = true
                        },
                        enabled = canChooseExisting,
                        loading = isCheckingChoices
                    )

                    CharacterChoiceButton(
                        label = stringResource(Res.string.choose_premade),
                        icon = Icons.Default.Groups3,
                        onClick = {
                            characterViewModel.loadPreMades()
                            showPreMadeDialog = true
                        },
                        enabled = canChoosePreMade,
                        loading = isCheckingChoices
                    )
                }
            }
        }

        if (showCreateDialog) {
            CreateCharacterInTableDialog(
                viewModel = characterViewModel,
                onDismiss = { showCreateDialog = false }
            )
        }
        if (showSelectDialog) {
            when (val state = charactersState) {
                is AsyncUiState.Success -> SelectCharacterDialog(
                    characters = state.data,
                    viewModel = characterViewModel,
                    onDismiss = { showSelectDialog = false }
                )

                is AsyncUiState.Error -> ChoiceLoadingDialog(
                    title = stringResource(Res.string.error_loading_characters),
                    error = state.message,
                    onDismiss = { showSelectDialog = false }
                )

                else -> ChoiceLoadingDialog(
                    title = stringResource(Res.string.loading),
                    onDismiss = { showSelectDialog = false }
                )
            }
        }
        if (showPreMadeDialog) {
            when (val state = preMadesState) {
                is AsyncUiState.Success -> SelectPreMadeDialog(
                    preMades = state.data,
                    viewModel = characterViewModel,
                    onDismiss = { showPreMadeDialog = false }
                )

                is AsyncUiState.Error -> ChoiceLoadingDialog(
                    title = stringResource(Res.string.error_loading_premades),
                    error = state.message,
                    onDismiss = { showPreMadeDialog = false }
                )

                else -> ChoiceLoadingDialog(
                    title = stringResource(Res.string.loading),
                    onDismiss = { showPreMadeDialog = false }
                )
            }
        }
        return
    }

    val refreshedCharacter by characterViewModel.refreshedCharacter.collectAsStateWithLifecycle()
    val displayCharacter = refreshedCharacter ?: character

    var selectedTabIndex by remember { mutableIntStateOf(0) }
    var showAvatarFullscreen by remember { mutableStateOf(false) }
    var showRenameDialog by remember { mutableStateOf(false) }

    val updateImageState by characterViewModel.updateImageState.collectAsStateWithLifecycle()
    val updateNameState by characterViewModel.updateNameState.collectAsStateWithLifecycle()

    val launchImagePicker = rememberImagePickerLauncher { imageData ->
        characterViewModel.updateCharacterImage(displayCharacter, imageData)
    }

    LaunchedEffect(updateImageState) {
        if (updateImageState is AsyncUiState.Success) characterViewModel.resetUpdateImageState()
    }
    LaunchedEffect(updateNameState) {
        if (updateNameState is AsyncUiState.Success) {
            characterViewModel.resetUpdateNameState()
            showRenameDialog = false
        }
    }

    // En-tête repliable : le défilement vers le bas du contenu de l'onglet replie d'abord
    // l'avatar (la barre d'onglets remonte alors en haut), et il ne se redéploie qu'une fois
    // le contenu revenu tout en haut (le surplus de défilement est alors rendu à l'en-tête).
    var headerHeightPx by remember { mutableIntStateOf(0) }
    var headerOffsetPx by remember { mutableFloatStateOf(0f) }
    val headerScrollConnection = remember {
        object : NestedScrollConnection {
            private fun consumeHeaderScroll(delta: Float): Offset {
                val newOffset = (headerOffsetPx + delta).coerceIn(-headerHeightPx.toFloat(), 0f)
                val consumed = newOffset - headerOffsetPx
                headerOffsetPx = newOffset
                return Offset(0f, consumed)
            }

            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset =
                if (available.y < 0f) consumeHeaderScroll(available.y) else Offset.Zero

            override fun onPostScroll(
                consumed: Offset,
                available: Offset,
                source: NestedScrollSource
            ): Offset =
                if (available.y > 0f) consumeHeaderScroll(available.y) else Offset.Zero
        }
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .nestedScroll(headerScrollConnection),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clipToBounds()
                .layout { measurable, constraints ->
                    val placeable = measurable.measure(
                        constraints.copy(minHeight = 0, maxHeight = Constraints.Infinity)
                    )
                    val offset = headerOffsetPx.roundToInt()
                        .coerceIn(-placeable.height, 0)
                    layout(placeable.width, placeable.height + offset) {
                        placeable.place(0, offset)
                    }
                }
                .onSizeChanged { headerHeightPx = it.height }
                .graphicsLayer {
                    alpha = if (headerHeightPx == 0) 1f
                    else 1f + headerOffsetPx / headerHeightPx
                }
                .padding(horizontal = 24.dp, vertical = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Box(modifier = Modifier.size(120.dp), contentAlignment = Alignment.BottomEnd) {
                CharacterAvatar(
                    imageUrl = displayCharacter.signedImageUrl,
                    characterName = displayCharacter.name,
                    size = 120.dp,
                    onClick = if (displayCharacter.signedImageUrl != null && updateImageState !is AsyncUiState.Loading) {
                        { showAvatarFullscreen = true }
                    } else null
                )
                FilledIconButton(
                    onClick = { launchImagePicker() },
                    modifier = Modifier.size(32.dp),
                    enabled = updateImageState !is AsyncUiState.Loading
                ) {
                    if (updateImageState is AsyncUiState.Loading) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            strokeWidth = 2.dp,
                            color = MaterialTheme.colorScheme.onPrimary
                        )
                    } else {
                        Icon(
                            imageVector = Icons.Default.AddAPhoto,
                            contentDescription = stringResource(Res.string.edit_avatar),
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text(
                    text = displayCharacter.name,
                    style = MaterialTheme.typography.headlineSmall
                )
                IconButton(
                    onClick = { showRenameDialog = true },
                    modifier = Modifier.size(32.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Edit,
                        contentDescription = stringResource(Res.string.edit_character_name),
                        modifier = Modifier.size(18.dp),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }

        val tabs = remember(isFreeConstruction, isCharacterSheetAvailable) {
            buildList {
                add(CharacterTabKind.INFO)
                if (isCharacterSheetAvailable && isFreeConstruction) add(CharacterTabKind.SHEET)
            }
        }
        val safeIndex = selectedTabIndex.coerceIn(0, tabs.lastIndex)

        SecondaryTabRow(selectedTabIndex = safeIndex) {
            tabs.forEachIndexed { index, kind ->
                Tab(
                    selected = safeIndex == index,
                    onClick = { selectedTabIndex = index },
                    text = { Text(stringResource(kind.labelRes())) }
                )
            }
        }

        when (tabs[safeIndex]) {
            CharacterTabKind.INFO -> CharacterInfoContent(
                character = displayCharacter,
                modifier = Modifier.fillMaxSize()
            )

            CharacterTabKind.SHEET -> CharacterSheetTab(
                character = displayCharacter,
                viewModel = characterViewModel,
                modifier = Modifier.fillMaxSize()
            )
        }
    }

    if (showRenameDialog) {
        RenameCharacterDialog(
            currentName = displayCharacter.name,
            updateState = updateNameState,
            onConfirm = { newName ->
                characterViewModel.updateCharacterName(
                    displayCharacter,
                    newName
                )
            },
            onDismiss = {
                characterViewModel.resetUpdateNameState()
                showRenameDialog = false
            }
        )
    }

    if (showAvatarFullscreen && displayCharacter.signedImageUrl != null) {
        ZoomableImageDialog(
            imageUrl = displayCharacter.signedImageUrl,
            contentDescription = displayCharacter.name,
            onDismiss = { showAvatarFullscreen = false }
        )
    }
}

private enum class CharacterTabKind { INFO, SHEET }

private fun CharacterTabKind.labelRes() = when (this) {
    CharacterTabKind.INFO -> Res.string.character_informations
    CharacterTabKind.SHEET -> Res.string.character_sheet_tab
}